import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Charts for the reporting console.
 *
 * Three rules hold everywhere here. Colour carries identity, never magnitude —
 * a series keeps its hue when a filter removes its neighbours. Series colours
 * and status colours never borrow each other: blue/orange/aqua mean "which
 * series", red means "breached", and nothing else. And every chart can be read
 * as a table, because three of these hues sit below 3:1 against the light
 * surface and a colour-only chart would fail the operator using it.
 */

export interface Series {
  key: string;
  label: string;
  color: string;
}

export function ChartFrame({
  title,
  subtitle,
  series,
  table,
  children,
}: {
  title: string;
  subtitle?: string;
  series: Series[];
  /** The same numbers as a table — the accessibility path, not a fallback. */
  table: ReactNode;
  children: ReactNode;
}) {
  const [asTable, setAsTable] = useState(false);
  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>{title}</h2>
          {subtitle && <div className="faint">{subtitle}</div>}
        </div>
        <div className="spacer" />
        {series.length > 1 && (
          <div className="row tight">
            {series.map((item) => (
              <span key={item.key} className="row tight faint" style={{ gap: 4 }}>
                <i className="dot" style={{ background: item.color, color: item.color }} />
                {item.label}
              </span>
            ))}
          </div>
        )}
        <button className="btn ghost sm" onClick={() => setAsTable((v) => !v)}>
          {asTable ? 'Chart' : 'Table'}
        </button>
      </div>
      <div className="card-body">{asTable ? table : children}</div>
    </section>
  );
}

interface StackedDatum {
  label: string;
  fullLabel: string;
  values: number[];
}

/**
 * Stacked bars over time. Segments are separated by a 2px surface gap so
 * adjacent fills never merge into one block, and the top of each column is
 * rounded — the data-end, not every corner.
 */
export function StackedBars({
  data,
  series,
  height = 200,
}: {
  data: StackedDatum[];
  series: Series[];
  height?: number;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(720);

  // The plot is drawn in real pixels rather than a stretched viewBox: scaling a
  // viewBox non-uniformly to fill the column distorts the tick labels along
  // with the bars, which is how axis text ends up unreadable.
  useEffect(() => {
    const element = wrapRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const totals = data.map((d) => d.values.reduce((sum, value) => sum + value, 0));
  const max = Math.max(1, ...totals);
  const padLeft = 34;
  const padBottom = 4;
  const padTop = 8;
  const plotHeight = height - padBottom - padTop;
  const slot = Math.max(6, (width - padLeft) / Math.max(1, data.length));
  // Thin marks: a bar never grows past 26px however few buckets there are.
  const barWidth = Math.min(26, Math.max(3, slot * 0.6));

  const ticks = [0, 0.5, 1].map((fraction) => Math.round(max * fraction));

  if (data.length === 0) return <p className="empty">No calls in this range.</p>;

  return (
    <div style={{ position: 'relative' }} ref={wrapRef}>
      <svg width={width} height={height} role="img">
        {ticks.map((tick) => {
          const y = padTop + plotHeight - (tick / max) * plotHeight;
          return (
            <g key={tick}>
              <line x1={padLeft} y1={y} x2={width} y2={y} stroke="var(--chart-grid)" strokeWidth={1} />
            </g>
          );
        })}

        {data.map((datum, index) => {
          let cursor = padTop + plotHeight;
          const x = padLeft + index * slot + (slot - barWidth) / 2;
          return (
            <g
              key={datum.label}
              onMouseEnter={() => setHover(index)}
              onMouseLeave={() => setHover((current) => (current === index ? null : current))}
            >
              <rect x={padLeft + index * slot} y={padTop} width={slot} height={plotHeight} fill="transparent" />
              {datum.values.map((value, seriesIndex) => {
                if (value <= 0) return null;
                const segmentHeight = (value / max) * plotHeight;
                // 2px surface gap between stacked segments.
                const gap = seriesIndex === 0 ? 0 : 2;
                cursor -= segmentHeight;
                const y = cursor;
                const isTop = datum.values.slice(seriesIndex + 1).every((v) => v <= 0);
                return (
                  <rect
                    key={series[seriesIndex].key}
                    x={x}
                    y={y + gap / 2}
                    width={barWidth}
                    height={Math.max(1, segmentHeight - gap)}
                    rx={isTop ? 4 : 0}
                    fill={series[seriesIndex].color}
                    opacity={hover === null || hover === index ? 1 : 0.45}
                  />
                );
              })}
            </g>
          );
        })}

        <line
          x1={padLeft} y1={padTop + plotHeight} x2={width} y2={padTop + plotHeight}
          stroke="var(--chart-axis)" strokeWidth={1}
        />
        {ticks.map((tick) => (
          <text
            key={`label-${tick}`}
            x={padLeft - 3}
            y={padTop + plotHeight - (tick / max) * plotHeight + 2}
            textAnchor="end"
            fontSize={11}
            fill="var(--text-faint)"
          >
            {tick}
          </text>
        ))}
      </svg>

      <div className="row between faint" style={{ marginTop: 2 }}>
        <span>{data[0]?.fullLabel}</span>
        <span>{data[data.length - 1]?.fullLabel}</span>
      </div>

      {hover !== null && data[hover] && (
        <div
          className="card"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            padding: '6px 10px',
            fontSize: 12,
            pointerEvents: 'none',
          }}
        >
          <strong>{data[hover].fullLabel}</strong>
          {series.map((item, index) => (
            <div key={item.key} className="row tight" style={{ gap: 6 }}>
              <i className="dot" style={{ background: item.color, color: item.color }} />
              <span className="muted">{item.label}</span>
              <span className="spacer" />
              <b>{data[hover].values[index]}</b>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Horizontal magnitude bars. One hue for the whole series — length already
 * encodes size, so shading each bar differently would spend the colour channel
 * on information the chart shows twice. The breached portion is a status
 * colour, and is labelled as well as coloured.
 */
export function CategoryBars({
  data,
}: {
  data: { label: string; total: number; breached: number }[];
}) {
  if (data.length === 0) return <p className="empty">No incidents in this range.</p>;
  const max = Math.max(1, ...data.map((d) => d.total));

  return (
    <div className="stack tight">
      {data.map((datum) => {
        const width = (datum.total / max) * 100;
        const breachedWidth = datum.total > 0 ? (datum.breached / max) * 100 : 0;
        return (
          <div key={datum.label}>
            <div className="row between">
              <span className="truncate" style={{ maxWidth: '70%' }}>{datum.label}</span>
              <span className="mono">
                {datum.total}
                {datum.breached > 0 && (
                  <span style={{ color: 'var(--status-critical)' }}> · {datum.breached} breached</span>
                )}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 2, height: 8, marginTop: 2 }}>
              <div
                style={{
                  width: `${Math.max(0, width - breachedWidth)}%`,
                  background: 'var(--series-1)',
                  borderRadius: '2px 0 0 2px',
                }}
              />
              {breachedWidth > 0 && (
                <div
                  style={{
                    width: `${breachedWidth}%`,
                    background: 'var(--status-critical)',
                    borderRadius: '0 2px 2px 0',
                  }}
                  title={`${datum.breached} breached the dispatch SLA`}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** A single number is not a chart. When the story is one figure, this is the form. */
export function StatTile({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail?: string;
  tone?: 'critical' | 'normal';
}) {
  return (
    <div className="tile">
      <div className="label">{label}</div>
      <div className="value" style={tone === 'critical' ? { color: 'var(--status-critical)' } : undefined}>
        {value}
      </div>
      {detail && <div className="delta">{detail}</div>}
    </div>
  );
}
